/**
 * LeaveManagerGuard
 *
 * Route guard for /leave/team, /leave/approvals, and other admin-only leave paths.
 *
 * Uses the same lv2_leave_approvals permission check as LeaveViewSwitcher so
 * the two stay in sync: if the Roles UI removes the permission, both the tabs
 * AND the direct URL are blocked.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from 'src/hooks/usePermissions';
import { useAuth } from 'src/hooks/useAuth';
import SuspenseLoader from 'src/components/SuspenseLoader';

interface LeaveManagerGuardProps {
  children: React.ReactNode;
  /** Permission key to check. Defaults to lv2_leave_approvals. */
  permissionKey?: string;
}

const LeaveManagerGuard: React.FC<LeaveManagerGuardProps> = ({
  children,
  permissionKey = 'lv2_leave_approvals',
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { authorisation, isLoading: permLoading } = usePermissions(permissionKey, false);
  const canAccess = authorisation.isShow || authorisation.isView || authorisation.isfullAccess;

  // Wait for auth and permissions to hydrate (avoids flash-redirect on refresh).
  if (authLoading || permLoading) {
    return <SuspenseLoader />;
  }

  // Not yet authenticated — let the auth layer handle redirection.
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Authenticated but no permission → redirect to own leave home.
  if (!canAccess) {
    return <Navigate to="/leave/my" replace />;
  }

  return <>{children}</>;
};

export default LeaveManagerGuard;
